import { ONE_DAY } from "./constants/time";

export function scrollToSection(sectionId: string) {
  const section = document.getElementById(sectionId);
  if (section) {
    section.scrollIntoView({ behavior: "smooth" });
  }
}

export function createNavigationProps(link?: string) {
  if (link?.startsWith("#")) {
    return { onClick: () => scrollToSection(link.substring(1)) };
  }
  if (link?.startsWith("mailto:")) {
    return { href: link };
  }
  return { routerLink: link };
}

export function formatCentsToDollars(cents: number = 0): string {
  // eslint-disable-next-line no-magic-numbers
  const dollars = cents / 100;
  return dollars.toLocaleString("en-US", {
    currency: "USD",
    style: "currency",
  });
}

/**
 * Find an object in an array, amd move it to the end of the array
 * @param array
 * @param key
 * @param value
 * @returns reordered array
 */
export function moveItemToEnd<T, K extends keyof T>(
  array: T[],
  key: K,
  value: T[K]
): T[] {
  const index = array.findIndex((item) => item[key] === value);
  if (index === -1) {
    return array; // Nem található ilyen elem
  }
  const [item] = array.splice(index, 1);
  array.push(item);

  return array;
}

/**
 * Determines the maximum number of decimal places present
 * in any number within the given array.
 *
 * Examples:
 * - [1, 2, 3] → 0
 * - [1.1, 2.34, 5.678] → 3
 * - [2.3, 4, 5.12] → 2
 *
 * @param {number[]} numbers - An array of numbers to analyze.
 * @returns {number} The highest number of decimal places found.
 */
export function maxDecimalPlaces(numbers: number[]): number {
  let maxDecimals = 0;

  for (const num of numbers) {
    // eslint-disable-next-line prefer-destructuring
    const decimalPart = num.toString().split(".")[1];
    if (decimalPart) {
      maxDecimals = Math.max(maxDecimals, decimalPart.length);
    }
  }

  return maxDecimals;
}

/**
 * Rounds a number to the maximum number of decimal places
 *
 * @param {number} value - The number to round.
 * @param {number[]} decimals - Number of decimals
 * @returns {number} The rounded number.
 */
export function roundToMaxDecimals(value: number, decimals: number): number {
  const factor = 10 ** decimals; // eslint-disable-line no-magic-numbers
  return Math.round(value * factor) / factor;
}

/** Coerce a value to a finite number for display or chart input; otherwise `fallback`. */
export function coerceFiniteNumber(value: unknown, fallback = 0): number {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

/** True when `value` is a finite number (excludes NaN, Infinity, and non-numbers). */
export function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * Calculates the number of days left in a trial period
 *
 * @param {string | Date} endDateTime - The end date/time of the trial (ISO string or Date object)
 * @returns {number} The number of days left in the trial (rounded up to the nearest day)
 */
export function calculateDaysLeftInTrial(endDateTime: string | Date): number {
  const endDate = new Date(endDateTime);
  const now = new Date();
  const timeDifference = endDate.getTime() - now.getTime();

  // Convert milliseconds to days and round up
  const daysLeft = Math.ceil(timeDifference / ONE_DAY);

  // Return 0 if the trial has already expired
  return Math.max(0, daysLeft);
}
